import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { isRegisteredVisitor, openAivioChat } from "./chat/chatApi";
import WalkthroughConnect from "./WalkthroughConnect";
import "./BudgetChatWalkthrough.css";

// Hero product walkthrough. One clock drives the whole scene, so it can pause (off-screen, hidden tab,
// the pause button, a visitor opening a source) and seek without timers drifting apart.
// Figures from Budget at a Glance 2022–⁠23 (p. 5, item 17) and Budget Speech 2022–⁠23 (p. 24, para 118).
const QUESTION = "How has India’s fiscal deficit changed since 2020–⁠21?";
const FOLLOW_UP = "What about the 2026–⁠27 budget?";
const ACT_REQUEST = "Summarize this for the finance team on Teams.";
const ACT_AGENTS = [
  { name: "Drafting agent", task: "writes the summary" },
  { name: "Formatting agent", task: "adds the figures and sources" },
  { name: "Teams connector", task: "prepares the post with your Microsoft 365 permission" },
];
const ACT_AGENT_MS = 1000;
const ANSWER = "The fiscal deficit for 2022–⁠23 is estimated at ₹16,61,196 crore, or 6.4% of GDP.".split(" ");
const ROWS = [
  { period: "Actual · 2020–⁠21", value: 1818291 },
  { period: "Budget · 2021–⁠22", value: 1506812 },
  { period: "Revised · 2021–⁠22", value: 1591089 },
  { period: "Budget · 2022–⁠23", value: 1661196 },
];
const MAX_ROW = Math.max(...ROWS.map((row) => row.value));
const SOURCES = {
  glance: {
    n: 1, file: "Budget at a Glance 2022–⁠23", page: 5, section: "Item 17 · Fiscal Deficit · ₹ crore",
    why: "Lists the fiscal deficit as actual, budget and revised estimates across three years.",
    quote: "Fiscal Deficit: 18,18,291 (Actuals 2020–⁠21) · 15,91,089 (Revised 2021–⁠22) · 16,61,196 (Budget 2022–⁠23)",
  },
  speech: {
    n: 2, file: "Budget Speech 2022–⁠23", page: 24, section: "Para 118 · Fiscal management",
    why: "The Finance Minister’s speech states the deficit as a share of GDP.",
    quote: "“The Fiscal Deficit in 2022-23 is estimated at 6.4 per cent of GDP, which is consistent with the broad path of fiscal consolidation.”",
  },
};

// Timeline in milliseconds, paced so each step can be read before the next begins.
const CHAR_MS = 55;
const WORD_MS = 165;
const ROW_MS = 280;
const THINK_MS = 1100;
const T = { type: 900 };
T.send = T.type + QUESTION.length * CHAR_MS + 600;
T.think = T.send + 500;
T.answer = T.think + 3 * THINK_MS + 700;
T.table = T.answer + ANSWER.length * WORD_MS + 400;
T.summary = T.table + ROWS.length * ROW_MS + 500;
T.sources = T.summary + 1300;
T.cursor = T.sources + 1800;
T.click = T.cursor + 1300;
T.open = T.click + 200;
T.close = T.open + 6000;
// Second turn: a public visitor asks for a year that needs a login.
T.type2 = T.close + 1000;
T.send2 = T.type2 + FOLLOW_UP.length * CHAR_MS + 600;
T.lock = T.send2 + 1600;
// Third turn: agents act on the answer, and a person approves before anything is sent.
T.type3 = T.lock + 4200;
T.send3 = T.type3 + ACT_REQUEST.length * CHAR_MS + 600;
T.plan = T.send3 + 500;
T.draft = T.plan + ACT_AGENTS.length * ACT_AGENT_MS + 400;
T.cursor3 = T.draft + 1700;
T.click3 = T.cursor3 + 1300;
T.posted = T.click3 + 250;
// Closing scene: the systems Aivio's agents can work in.
T.connect = T.posted + 3200;
T.fade = T.connect + 10000;
T.end = T.fade + 700;
const THINK_STEPS = [
  { label: "Searching the 2022–⁠23 budget documents", at: T.think },
  { label: "Reading Budget at a Glance · Fiscal Deficit", at: T.think + THINK_MS },
  { label: "Checking Budget Speech · para 118", at: T.think + 2 * THINK_MS },
];
const CHAPTERS = [{ label: "Ask", at: 0 }, { label: "Search", at: T.think }, { label: "Answer", at: T.answer }, { label: "Verify", at: T.cursor }, { label: "Access", at: T.type2 }, { label: "Act", at: T.type3 }, { label: "Connect", at: T.connect }];
// The demo pointer: to the first source, then to the approval button.
const CURSOR_RUNS = [{ start: T.cursor, click: T.click, end: T.open + 500 }, { start: T.cursor3, click: T.click3, end: T.posted + 400 }];
const REST = T.cursor - 1; // what reduced-motion visitors see: the full answer, ready to explore

const prefersReducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const crore = (value) => `₹${value.toLocaleString("en-IN")} cr`;
const typedAt = (t, start, end, text) => (t >= start && t < end ? text.slice(0, Math.floor((t - start) / CHAR_MS)) : "");

function AccessCard() {
  if (isRegisteredVisitor()) {
    return (
      <div className="hw-lock is-open">
        <span className="hw-lock-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 7.5-2" /></svg></span>
        <div>
          <span className="hw-lock-tag">Role-based access · Signed in</span>
          <p>The fiscal deficit for <b>2026–⁠27</b> is estimated at <b>4.3% of GDP</b>, down from 6.4% in 2022–⁠23. Because you’re signed in, I can use every budget from 2020–⁠21 to 2026–⁠27.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="hw-lock">
      <span className="hw-lock-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg></span>
      <div>
        <span className="hw-lock-tag">Role-based access · Public visitor</span>
        <p>Budgets from <b>2023–⁠24 to 2026–⁠27</b> are available after you log in. Right now I can help with 2020–⁠21 to 2022–⁠23.</p>
        <Link to="/login" className="hw-lock-link">Log in to unlock <span aria-hidden="true">→</span></Link>
      </div>
    </div>
  );
}

function TeamsDraft({ posted, pressed, approveRef, onApprove }) {
  return (
    <div className={`hw-draft${posted ? " is-posted" : ""}`}>
      <div className="hw-draft-head"><span className="hw-teams" aria-hidden="true">T</span><b>Microsoft Teams</b><small>#finance-team · {posted ? "Posted as you" : "Draft · posts as you"}</small></div>
      <p><b>Fiscal deficit update:</b> the 2022–⁠23 Budget Estimate is ₹16,61,196 crore (6.4% of GDP), down from 6.9% in the 2021–⁠22 Revised Estimate.</p>
      <small className="hw-draft-src">Sources: Budget at a Glance 2022–⁠23, p. 5 · Budget Speech 2022–⁠23, p. 24</small>
      <div className="hw-draft-foot">
        {posted ? <span className="hw-posted"><i>✓</i>Posted to #finance-team · just now</span> : (
          <>
            <span className="hw-needs">Needs your approval</span>
            <button type="button" ref={approveRef} className={`hw-approve${pressed ? " is-pressed" : ""}`} onClick={onApprove}>Approve and send</button>
          </>
        )}
      </div>
    </div>
  );
}

function Cite({ n, onOpen }) {
  return <button type="button" className="hw-cite" onClick={() => onOpen(n === 1 ? "glance" : "speech")} aria-label={`Open source ${n}`}>{n}</button>;
}

function SourcePane({ id, onClose, highlight }) {
  const source = SOURCES[id];
  return (
    <aside className="hw-pane" aria-label="Source document preview">
      <div className="hw-pane-head">
        <span className="hw-pane-kicker">Source {source.n} of 2 <i>PDF</i></span>
        <button type="button" onClick={onClose} aria-label="Close source">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
        </button>
      </div>
      <h3>{source.file}.pdf</h3>
      <div className="hw-pane-meta">Page {source.page} · Government of India</div>
      <div className="hw-pane-why"><b>Why this source</b>{source.why}</div>
      <div className="hw-page">
        <div className="hw-page-title">{source.section}</div>
        <span className="hw-line" /><span className="hw-line is-short" />
        <p className={`hw-quote${highlight ? " is-on" : ""}`}>{source.quote}</p>
        <span className="hw-line" /><span className="hw-line" /><span className="hw-line is-short" />
        <div className="hw-page-no">p. {source.page}</div>
      </div>
    </aside>
  );
}

function BudgetChatWalkthrough() {
  const frameRef = useRef(null);
  const chipRef = useRef(null);
  const approveRef = useRef(null);
  const sendRef = useRef(null);
  const fillRef = useRef(null);
  const clock = useRef({ t: 0, last: 0 });
  const [t, setT] = useState(0);
  const [paused, setPaused] = useState(false);
  const [inView, setInView] = useState(true);
  const [tabVisible, setTabVisible] = useState(true);
  const [manualSource, setManualSource] = useState(null);
  const [cursor, setCursor] = useState(null);
  const reduced = useRef(prefersReducedMotion());
  const playing = !reduced.current && !paused && inView && tabVisible && manualSource === null;

  const seek = useCallback((time) => {
    clock.current.t = time;
    setT(time);
  }, []);

  useEffect(() => {
    if (reduced.current) seek(REST);
  }, [seek]);

  // The clock only advances while the scene is playing.
  useEffect(() => {
    if (!playing) return undefined;
    let frame;
    clock.current.last = performance.now();
    const tick = (now) => {
      const c = clock.current;
      c.t += Math.min(now - c.last, 100);
      c.last = now;
      if (c.t >= T.end) c.t = 0;
      if (fillRef.current) fillRef.current.style.transform = `scaleX(${c.t / T.end})`;
      setT((prev) => (Math.abs(prev - c.t) >= 30 || c.t < prev ? c.t : prev));
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [playing]);

  useEffect(() => {
    if (fillRef.current) fillRef.current.style.transform = `scaleX(${t / T.end})`;
  }, [t]);

  // Pause off-screen and in background tabs.
  useEffect(() => {
    const node = frameRef.current;
    const onVisibility = () => setTabVisible(!document.hidden);
    let observer;
    if (node && "IntersectionObserver" in window) {
      observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.2 });
      observer.observe(node);
    }
    document.addEventListener("visibilitychange", onVisibility);
    return () => { observer?.disconnect(); document.removeEventListener("visibilitychange", onVisibility); };
  }, []);

  // The pointer glides from the composer to its target, then clicks it.
  const cursorRun = manualSource === null ? CURSOR_RUNS.findIndex((run) => t >= run.start && t < run.end) : -1;
  useLayoutEffect(() => {
    if (cursorRun < 0) { setCursor(null); return undefined; }
    const frame = frameRef.current?.getBoundingClientRect();
    const from = sendRef.current?.getBoundingClientRect();
    const to = (cursorRun === 0 ? chipRef : approveRef).current?.getBoundingClientRect();
    if (!frame || !from || !to) return undefined;
    const { click } = CURSOR_RUNS[cursorRun];
    setCursor({ x: from.left - frame.left + 8, y: from.top - frame.top + 8, moving: false, click });
    const raf = window.requestAnimationFrame(() => setCursor({ x: to.left - frame.left + to.width * 0.72, y: to.top - frame.top + to.height * 0.55, moving: true, click }));
    return () => window.cancelAnimationFrame(raf);
  }, [cursorRun]);

  const openSource = (id) => setManualSource((current) => (current === id ? null : id));
  const resume = () => { setManualSource(null); setPaused(false); };
  const replay = () => { setManualSource(null); setPaused(false); seek(0); };

  const typed = typedAt(t, T.type, T.send, QUESTION) || typedAt(t, T.type2, T.send2, FOLLOW_UP) || typedAt(t, T.type3, T.send3, ACT_REQUEST);
  const posted = t >= T.posted;
  const sent = t >= T.send;
  const words = t < T.answer ? 0 : Math.min(ANSWER.length, Math.floor((t - T.answer) / WORD_MS) + 1);
  const answerDone = words === ANSWER.length;
  const rowsShown = t < T.table ? 0 : Math.min(ROWS.length, Math.floor((t - T.table) / ROW_MS) + 1);
  const autoSource = t >= T.open && t < T.close ? "glance" : null;
  const source = manualSource || autoSource;
  const chapter = CHAPTERS.reduce((current, item, index) => (t >= item.at ? index : current), 0);
  const fading = t >= T.fade;
  const connect = t >= T.connect;

  return (
    <div className="hw" id="product">
      <div className={`hw-frame${source ? " has-source" : ""}`} ref={frameRef} aria-label="Animated Aivio conversation with clickable budget citations and document preview" role="group">
        <div className="hw-top">
          <span className="hw-mark">✦</span>
          <span className="hw-title"><b>Aivio</b><small><i />{t >= T.type3 && !connect ? "Signed in · Microsoft Teams connected" : "Knowledge agent · India Budget"}</small></span>
          <span className="hw-top-icons" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" /></svg>
            <svg viewBox="0 0 24 24"><path d="m6 9 6 6 6-6" /></svg>
          </span>
        </div>
        <div className="hw-progress" aria-hidden="true"><span ref={fillRef} /></div>

        <div className={`hw-thread${fading || connect ? " is-fading" : ""}`}>
          <div className="hw-thread-inner">
            {!sent && (
              <div className="hw-welcome">
                <strong>Hi, I’m Aivio.</strong>
                <span>Ask about India’s Union Budget and inspect every source.</span>
                <div><i>Fiscal deficit</i><i>Capital expenditure</i><i>Tax changes</i></div>
              </div>
            )}
            {sent && <div className="hw-user">{QUESTION}</div>}
            {sent && (
              <div className="hw-bot">
                <div className="hw-who"><span className="hw-mark is-small">✦</span><b>Aivio</b></div>
                {t < T.answer ? (
                  <div className="hw-think">
                    {THINK_STEPS.filter((step) => t >= step.at).map((step, index, shown) => {
                      const done = index < shown.length - 1 || t >= step.at + 650;
                      return <div className={`hw-think-row${done ? " is-done" : ""}`} key={step.label}><i>{done ? "✓" : ""}</i>{step.label}</div>;
                    })}
                  </div>
                ) : (
                  <>
                    <div className="hw-read"><i>✓</i>Read 2 budget documents</div>
                    <div className="hw-answer">
                      <p>{ANSWER.slice(0, words).map((word, index) => <span className="hw-word" key={index}>{word} </span>)}{answerDone ? <Cite n={1} onOpen={openSource} /> : <i className="hw-caret" />}</p>
                      {rowsShown > 0 && (
                        <div className="hw-table" role="table" aria-label="Fiscal deficit by year">
                          {ROWS.slice(0, rowsShown).map((row, index) => (
                            <div className={`hw-row${index === ROWS.length - 1 ? " is-latest" : ""}`} role="row" key={row.period}>
                              <span role="cell">{row.period}</span>
                              <span className="hw-bar" aria-hidden="true"><i style={{ "--w": row.value / MAX_ROW }} /></span>
                              <b role="cell">{crore(row.value)}</b>
                            </div>
                          ))}
                        </div>
                      )}
                      {t >= T.summary && <p className="hw-summary">That is <b>₹1,57,095 crore lower</b> than the 2020–⁠21 actual, and down from <b>6.9%</b> of GDP in the 2021–⁠22 Revised Estimate.<Cite n={1} onOpen={openSource} /><Cite n={2} onOpen={openSource} /></p>}
                    </div>
                    {t >= T.sources && (
                      <div className="hw-sources">
                        {Object.entries(SOURCES).map(([id, item]) => (
                          <button type="button" key={id} ref={item.n === 1 ? chipRef : undefined} onClick={() => openSource(id)}
                            className={`hw-chip${source === id ? " is-active" : ""}${id === "glance" && t >= T.click && t < T.click + 450 && !manualSource ? " is-pressed" : ""}`}>
                            <b>{item.n}</b><em>PDF</em>{item.file}<small>p. {item.page}</small>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            {t >= T.send2 && <div className="hw-user">{FOLLOW_UP}</div>}
            {t >= T.send2 && (
              <div className="hw-bot">
                <div className="hw-who"><span className="hw-mark is-small">✦</span><b>Aivio</b></div>
                {t < T.lock ? <div className="hw-think"><div className="hw-think-row"><i />Checking your access to 2026–⁠27</div></div> : <AccessCard />}
              </div>
            )}
            {/* Acting in Teams needs a signed-in user with Microsoft 365 connected, so the scene shows that first. */}
            {t >= T.type3 && (
              <div className="hw-session" role="note">
                <span className="hw-session-dot" aria-hidden="true" />
                <b>Signed in</b> to your organization’s Aivio<span className="hw-session-sep" aria-hidden="true">·</span>
                <span className="hw-teams is-mini" aria-hidden="true">T</span>Microsoft Teams connected
              </div>
            )}
            {t >= T.send3 && <div className="hw-user">{ACT_REQUEST}</div>}
            {t >= T.send3 && (
              <div className="hw-bot">
                <div className="hw-who"><span className="hw-mark is-small">✦</span><b>Aivio</b><span className="hw-example">Example workflow</span></div>
                {t < T.draft ? (
                  <div className="hw-think">
                    <div className="hw-plan-head">Coordinator assigned {ACT_AGENTS.length} agents</div>
                    {ACT_AGENTS.filter((_, index) => t >= T.plan + index * ACT_AGENT_MS).map((agent, index) => {
                      const done = t >= T.plan + index * ACT_AGENT_MS + 850;
                      return <div className={`hw-think-row${done ? " is-done" : ""}`} key={agent.name}><i>{done ? "✓" : ""}</i><span><b>{agent.name}</b> {agent.task}</span></div>;
                    })}
                  </div>
                ) : (
                  <>
                    <div className="hw-read"><i>✓</i>{ACT_AGENTS.length} agents prepared this{posted ? "" : " · waiting for your approval"}</div>
                    <TeamsDraft posted={posted} pressed={t >= T.click3 && t < T.posted && manualSource === null} approveRef={approveRef} onApprove={() => seek(T.posted)} />
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <div className={`hw-composer${connect ? " is-hidden" : ""}`}>
          <div className={`hw-input${typed ? " is-typing" : ""}`} onClick={() => openAivioChat()} role="presentation">
            <span>{typed || (sent ? "Ask a follow-up…" : "Ask about a policy, budget or document…")}{typed && <i className="hw-caret" />}</span>
            {t >= T.sources && !typed ? (
              <button type="button" className="hw-live" ref={sendRef} onClick={(event) => { event.stopPropagation(); openAivioChat(QUESTION.replace(/⁠/g, "")); }}>Try it live <span aria-hidden="true">→</span></button>
            ) : (
              <span className={`hw-send${typed ? " is-ready" : ""}`} ref={sendRef} aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M12 19V5M5 12l7-7 7 7" /></svg></span>
            )}
          </div>
          <div className="hw-disclaimer">Aivio can make mistakes. Check sources for important details.</div>
        </div>

        {connect && <WalkthroughConnect fading={fading} usedArea="COLLABORATION" />}

        {source && <SourcePane key={source} id={source} highlight={manualSource !== null || t >= T.open + 900} onClose={() => (manualSource ? setManualSource(null) : seek(T.close))} />}

        {cursor && (
          <span className={`hw-pointer${cursor.moving ? " is-moving" : ""}${t >= cursor.click ? " is-clicking" : ""}`} style={{ transform: `translate(${cursor.x}px, ${cursor.y}px)` }} aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="M5 3v16l4.5-4.2 2.8 6.2 2.7-1.2-2.8-6.1L19 13z" /></svg>
          </span>
        )}
      </div>

      <div className="hw-caption">
        <div className="hw-chapters" aria-label="Walkthrough steps">
          {CHAPTERS.map((item, index) => (
            <button type="button" key={item.label} className={index === chapter ? "is-current" : index < chapter ? "is-past" : ""} onClick={() => { setManualSource(null); seek(item.at); }} aria-current={index === chapter ? "step" : undefined}>{item.label}</button>
          ))}
        </div>
        {manualSource || paused ? (
          <button type="button" className="hw-play" onClick={resume}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg><span className="hw-play-label">Resume</span></button>
        ) : (
          <button type="button" className="hw-play" onClick={() => setPaused(true)} aria-label="Pause walkthrough"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h4v14H7zM13 5h4v14h-4z" /></svg><span className="hw-play-label">Pause</span></button>
        )}
        <button type="button" className="hw-play" onClick={replay} aria-label="Replay walkthrough"><svg viewBox="0 0 24 24" aria-hidden="true" className="is-stroke"><path d="M4 12a8 8 0 1 0 2.4-5.7M4 4v4.5h4.5" /></svg></button>
      </div>
    </div>
  );
}

export default BudgetChatWalkthrough;
